import { AppointmentStatus } from "@/constants/Booking";
import { RecurrenceValue } from "@/constants/Schedule";
import api, { CacheIdType, CacheTagType } from "@/store/api";
import { doesRecur } from "@/utils/schedule.util";

interface GetAppointmentTypesResponse {
  data?: BookingProviderType[];
  message?: string;
  status: string;
}

interface ProviderTypeAttendeeRequest {
  patientExternalId: string;
  typeGroup: string;
}

interface GetAppointmentAttendeesResponse {
  data: BookingAttendee[];
  message: string;
  status: string;
}

interface ScheduleAvailabilityRequest {
  appointmentType: string;
  availabilityEndDateTime: string;
  availabilityStartDateTime: string;
  hostExternalId: string;
  isPatientAttending: boolean;
  patientExternalId: string;
  providerAttendeeExternalIds?: string[];
  recurrenceFrequency?: EventRecurrence;
  supportAttendeeExternalIds?: string[];
  weekOffset?: number;
}

interface BookingAvailabilityBody extends Omit<
  ScheduleAvailabilityRequest,
  "recurrenceFrequency"
> {
  recurrenceFrequency?: string;
}

interface BookAppointmentRequest {
  appointmentEndDateTime: string;
  appointmentStartDateTime: string;
  appointmentType: string;
  hostExternalId: string;
  isPatient?: boolean;
  isPatientAttending: boolean;
  isRescheduleRequest: boolean;
  patientExternalId: string;
  providerAttendeeExternalIds?: string[];
  recurrenceFrequency?: EventRecurrence;
  supportAttendeeExternalIds?: string[];
}

interface BookAppointmentResponse {
  data?: string;
  status?: string;
}

interface BookAppointmentBody extends Omit<
  BookAppointmentRequest,
  "recurrenceFrequency"
> {
  recurrenceFrequency?: string;
  status: AppointmentStatus.Scheduled;
  timeZoneId: string;
}

interface ScheduleAvailabilityResponse {
  available_slots: TimeSlot[];
}

const bookingApi = api.injectEndpoints({
  endpoints: (build) => ({
    confirmAppointment: build.mutation<
      BookAppointmentResponse,
      BookAppointmentRequest
    >({
      // eslint-disable-next-line max-params
      invalidatesTags: (_result, _error, arg) => {
        // For reschedule requests, skip invalidation here. The new appointment
        // must not appear beside the still-active old one while the modal is
        // open. cancelAppointmentWithRescheduledAppointment's invalidatesTags
        // handles the cache refresh once the old appointment is cancelled.
        if (arg.isRescheduleRequest) {
          return [];
        }
        const tagId = arg.isPatientAttending ? arg.patientExternalId : "";
        return [
          // since the app user is booking an appointment,
          // that includes them as an attendee, always refresh their schedule
          { id: CacheIdType.AppUser, type: CacheTagType.AppointmentEvents },
          { id: tagId, type: CacheTagType.AppointmentEvents },
        ];
      },
      // eslint-disable-next-line max-params
      onQueryStarted: async (_arg, { queryFulfilled }) => {
        await queryFulfilled;
      },
      query: ({
        appointmentEndDateTime,
        appointmentStartDateTime,
        appointmentType,
        hostExternalId,
        isPatient,
        isPatientAttending,
        isRescheduleRequest,
        patientExternalId,
        providerAttendeeExternalIds,
        recurrenceFrequency,
        supportAttendeeExternalIds,
      }: BookAppointmentRequest) => {
        const body: BookAppointmentBody = {
          appointmentEndDateTime,
          appointmentStartDateTime,
          appointmentType,
          hostExternalId,
          isPatient,
          isPatientAttending,
          isRescheduleRequest,
          patientExternalId,
          providerAttendeeExternalIds,
          status: AppointmentStatus.Scheduled,
          supportAttendeeExternalIds,
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        if (doesRecur(recurrenceFrequency)) {
          body.recurrenceFrequency = recurrenceFrequency?.toLowerCase();
        }

        return {
          body,
          method: "POST",
          url: "/schedule/family/appointment",
        };
      },
    }),
    getBookingAvailability: build.query<
      TimeSlot[],
      ScheduleAvailabilityRequest
    >({
      query: ({
        appointmentType,
        availabilityEndDateTime,
        availabilityStartDateTime,
        hostExternalId,
        isPatientAttending,
        patientExternalId,
        providerAttendeeExternalIds,
        recurrenceFrequency,
        supportAttendeeExternalIds,
      }) => {
        const body: BookingAvailabilityBody = {
          appointmentType,
          availabilityEndDateTime,
          availabilityStartDateTime,
          hostExternalId,
          isPatientAttending,
          patientExternalId,
          providerAttendeeExternalIds,
          supportAttendeeExternalIds,
        };

        if (recurrenceFrequency !== RecurrenceValue.DoesNotRepeat) {
          body.recurrenceFrequency = recurrenceFrequency?.toLowerCase();
        }

        return {
          body,
          method: "POST",
          url: "/schedule/family/availability",
        };
      },

      transformResponse: (response?: ScheduleAvailabilityResponse) => {
        return response?.available_slots || [];
      },
    }),
    getPatientAppointmentTypes: build.query<
      BookingProviderType[],
      { patientExternalId: string }
    >({
      query: ({ patientExternalId }) => {
        return `/schedule/family/appointment-types/v2/${patientExternalId}`;
      },
      transformResponse: (response?: GetAppointmentTypesResponse) => {
        return response?.data || [];
      },
    }),
    getProviderTypeAttendees: build.query<
      BookingAttendee[],
      ProviderTypeAttendeeRequest
    >({
      query: ({ patientExternalId, typeGroup }) =>
        `/schedule/family/attendees/${patientExternalId}?type-group=${typeGroup}`,
      transformResponse: (response?: GetAppointmentAttendeesResponse) => {
        return response?.data || [];
      },
    }),
    getSchedulePermissions: build.query<SchedulePermissionResponse, void>({
      query: () => "/schedule/family/appointments/permissions",
    }),
  }),
});

const {
  useConfirmAppointmentMutation,
  useGetProviderTypeAttendeesQuery,
  useGetSchedulePermissionsQuery,
  useLazyGetBookingAvailabilityQuery,
  useLazyGetPatientAppointmentTypesQuery,
} = bookingApi;

export {
  useConfirmAppointmentMutation,
  useGetProviderTypeAttendeesQuery,
  useGetSchedulePermissionsQuery,
  useLazyGetBookingAvailabilityQuery,
  useLazyGetPatientAppointmentTypesQuery,
};
